<template>
  <div id="missions-plugin">
    <!-- No farm in context — the plugin is launched from inside a farm. -->
    <div v-if="!farmId" class="ms-no-farm">
      <p>Open Missions from one of your farms.</p>
      <router-link to="/">← Back to your farms</router-link>
    </div>

    <template v-else>
      <PluginHeader
        title="Missions"
        subtitle="Dispatch and monitor robot missions"
        icon="fas fa-robot"
      />

      <div class="container pt-3" style="max-width: 760px;">
        <!-- Safety framing, stated where missions are made. -->
        <p class="text-muted small mb-3">
          Missions are <strong>advisory instructions</strong>. The robot validates
          every path against its own limits and owns all safety behavior —
          publishing here dispatches work, it does not remotely control a machine.
        </p>

        <!-- CREATE -->
        <b-card class="mb-3" v-if="canManage">
          <p class="mb-2"><strong><i class="fas fa-plus-circle"></i> New mission</strong></p>
          <b-form-group label="Name" class="mb-2">
            <b-form-input v-model="form.name" placeholder="e.g. Row scout — Block 7 east"></b-form-input>
          </b-form-group>
          <b-form-group label="Path (a line dataset the robot will follow)" class="mb-2" description="One mission = one line. Vertex order is the drive order; the robot starts from wherever it is and heads to the first vertex.">
            <b-form-select v-model="form.pathDatasetId" :options="pathDatasetOptions"></b-form-select>
          </b-form-group>
          <div v-if="form.pathDatasetId" class="ms-path mb-3">
            <div v-if="path.loading" class="small text-muted"><i class="fas fa-sync fa-spin"></i> Checking path…</div>
            <div v-else-if="path.error" class="small text-danger"><i class="fas fa-exclamation-triangle"></i> {{ path.error }}</div>
            <div v-else-if="path.points.length" class="small">
              <span class="text-success"><i class="fas fa-check-circle"></i> Single line · <strong>{{ path.points.length }} waypoints</strong> · {{ pathLengthLabel }}</span>
              <span class="text-muted"> · <span class="ms-dot ms-dot-start"></span> start → <span class="ms-dot ms-dot-end"></span> end shown for "{{ form.direction === 'reverse' ? 'Reverse' : 'As drawn' }}"</span>
            </div>
            <div id="ms-path-map" class="ms-path-map mt-2" v-show="!path.error && path.points.length"></div>
          </div>
          <div class="d-flex" style="gap: 8px;">
            <b-form-group label="Asset ID" class="mb-2 flex-fill" description="Must match the robot's configured id (blank = any asset may claim).">
              <b-form-input v-model="form.assetId" placeholder="amiga-clerel-01"></b-form-input>
            </b-form-group>
            <b-form-group label="Asset label" class="mb-2 flex-fill">
              <b-form-input v-model="form.assetLabel" placeholder="CLEREL Amiga"></b-form-input>
            </b-form-group>
          </div>
          <div class="d-flex" style="gap: 8px;">
            <b-form-group label="Target speed (m/s)" class="mb-2 flex-fill">
              <b-form-input v-model.number="form.targetSpeedMps" type="number" step="0.1" min="0"></b-form-input>
            </b-form-group>
            <b-form-group label="Laps" class="mb-2 flex-fill">
              <b-form-input v-model.number="form.laps" type="number" min="1"></b-form-input>
            </b-form-group>
            <b-form-group label="Direction" class="mb-2 flex-fill">
              <b-form-select v-model="form.direction" :options="[{value:'as-drawn',text:'As drawn'},{value:'reverse',text:'Reverse'}]"></b-form-select>
            </b-form-group>
          </div>
          <b-form-group label="Notes" class="mb-2">
            <b-form-textarea v-model="form.notes" rows="2"></b-form-textarea>
          </b-form-group>
          <b-btn variant="primary" :disabled="!form.name || !form.pathDatasetId || savingDraft || path.loading || !!path.error || !path.points.length" @click="saveDraft">
            <i v-if="savingDraft" class="fas fa-sync fa-spin"></i>
            <span v-else>Save draft</span>
          </b-btn>
        </b-card>

        <!-- LIST -->
        <p class="mb-2"><strong><i class="fas fa-list"></i> Missions</strong></p>
        <div v-if="!missions.length" class="text-muted small mb-4">
          No missions yet. Create a draft above, then Publish it — the robot
          picks up published missions on its next poll.
        </div>
        <div class="list-group mb-5">
          <div
            v-for="m in missions"
            :key="m.id"
            class="list-group-item"
          >
            <div class="d-flex align-items-center" style="gap: 8px;">
              <span class="ms-chip" :class="'ms-' + m.status">
                <span v-if="m.status === 'running'" class="ms-hb" :class="{ fresh: isFresh(m) }"></span>
                {{ m.status }}
              </span>
              <strong class="flex-fill">{{ m.name }}</strong>
              <small class="text-muted">{{ m.assetLabel || m.assetId || 'any asset' }}</small>
            </div>
            <div class="small text-muted mt-1">
              Path: {{ datasetName(m.pathDatasetId) }}
              <span v-if="m.params"> · {{ m.params.targetSpeedMps }} m/s · {{ m.params.laps }} lap(s)</span>
              <span v-if="typeof m.progress === 'number'"> · {{ Math.round(m.progress * 100) }}%</span>
              <span v-if="m.failureReason" class="text-danger"> · {{ m.failureReason }}</span>
              <span v-if="m.results && m.results.summary"> · {{ m.results.summary }}</span>
            </div>
            <div class="mt-2" v-if="canManage">
              <b-btn v-if="m.status === 'draft'" size="sm" variant="success" class="mr-1" @click="setStatus(m, 'ready')">Publish</b-btn>
              <b-btn v-if="m.status === 'ready'" size="sm" variant="outline-secondary" class="mr-1" @click="setStatus(m, 'draft')">Unpublish</b-btn>
              <b-btn v-if="['ready','claimed','running'].includes(m.status)" size="sm" variant="outline-danger" class="mr-1" @click="setStatus(m, 'aborted')">Abort</b-btn>
              <b-btn v-if="m.status === 'running' || m.status === 'claimed'" size="sm" variant="outline-primary" class="mr-1" @click="viewLive">View live</b-btn>
              <b-btn v-if="canDelete && ['draft','ready','aborted'].includes(m.status)" size="sm" variant="outline-secondary" @click="removeMission(m)">Delete</b-btn>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import PluginHeader from '../_shared/PluginHeader.vue'
import firebase from 'firebase/compat/app'
import 'firebase/compat/storage'
// mapbox-gl is loaded globally in public/index.html (the app's map does the same);
// importing the npm build breaks the Vue CLI webpack parse.
const mapboxgl = window.mapboxgl // eslint-disable-line
const turf = require('@turf/turf')
const fb = require('@/firebaseConfig.js')

/**
 * Mirror of the server's buildWaypoints() contract (functions/developerApi.js):
 * one line per mission; points only with an explicit seq. Validating here
 * means a bad path is caught when it is picked, not when the robot polls.
 */
function pathFromGeoJson (gj) {
  const feats = Array.isArray(gj && gj.features) ? gj.features.filter(f => f && f.geometry) : []
  const lines = feats.filter(f => f.geometry.type === 'LineString' || f.geometry.type === 'MultiLineString')
  const pts = feats.filter(f => f.geometry.type === 'Point')
  if (lines.length) {
    const parts = []
    lines.forEach(f => {
      const ps = f.geometry.type === 'LineString' ? [f.geometry.coordinates] : f.geometry.coordinates
      ps.forEach(part => { if (Array.isArray(part) && part.length) parts.push(part) })
    })
    if (parts.length !== 1) {
      return { error: `A mission path must be a single line, but this dataset has ${lines.length} line feature${lines.length === 1 ? '' : 's'} with ${parts.length} parts. Split it into one dataset per mission, or merge the rows into one continuous line in drive order.` }
    }
    return { points: parts[0].map(c => [Number(c[0]), Number(c[1])]) }
  }
  if (!pts.length) return { error: 'This dataset has no line or point features.' }
  const seqOf = f => {
    const pr = f.properties || {}
    const v = pr.seq != null ? pr.seq : (pr.order != null ? pr.order : (pr.waypoint != null ? pr.waypoint : null))
    return v == null ? null : Number(v)
  }
  if (!pts.every(f => Number.isFinite(seqOf(f)))) {
    return { error: 'This dataset is points without a sequence. Missions need a line (vertex order = drive order), or a numeric seq property on every point. File order is not used because edits can reorder features.' }
  }
  return { points: pts.slice().sort((a, b) => seqOf(a) - seqOf(b)).map(f => [Number(f.geometry.coordinates[0]), Number(f.geometry.coordinates[1])]) }
}

export default {
  components: { PluginHeader },
  data () {
    return {
      missions: [],
      unsubscribe: null,
      datasets: [],
      path: { loading: false, error: '', points: [], datasetId: null },
      pathMap: null,
      savingDraft: false,
      now: Date.now(),
      nowTimer: null,
      form: {
        name: '',
        pathDatasetId: null,
        assetId: '',
        assetLabel: '',
        targetSpeedMps: 0.5,
        laps: 1,
        direction: 'as-drawn',
        notes: ''
      }
    }
  },
  computed: {
    farmId () {
      return this.$store.state.farm && this.$store.state.farm.id
    },
    role () {
      return this.$store.state.role
    },
    canManage () {
      return ['Owner', 'Administrator', 'Editor'].includes(this.role) ||
        (this.$store.state.farm && this.$store.state.farm.ownerId === (this.$store.state.currentUser && this.$store.state.currentUser.uid))
    },
    canDelete () {
      return ['Owner', 'Administrator'].includes(this.role) ||
        (this.$store.state.farm && this.$store.state.farm.ownerId === (this.$store.state.currentUser && this.$store.state.currentUser.uid))
    },
    pathDatasetOptions () {
      const opts = this.datasets.map(d => ({ value: d.id, text: d.name }))
      return [{ value: null, text: 'Choose a line dataset…' }].concat(opts)
    },
    orientedPoints () {
      return this.form.direction === 'reverse' ? this.path.points.slice().reverse() : this.path.points
    },
    pathLengthLabel () {
      if (this.path.points.length < 2) return ''
      const m = turf.length(turf.lineString(this.path.points), { units: 'kilometers' }) * 1000
      return m >= 1000 ? (m / 1000).toFixed(2) + ' km' : Math.round(m) + ' m'
    }
  },
  watch: {
    'form.pathDatasetId' (id) { this.loadPath(id) },
    'form.direction' () { this.drawPath() }
  },
  created () {
    if (!this.farmId) return
    // Mission list — the query MUST constrain farmId (rules provability).
    this.unsubscribe = fb.db.collection('missions')
      .where('farmId', '==', this.farmId)
      .onSnapshot(snap => {
        this.missions = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const at = (a.createdOn && a.createdOn.seconds) || 0
            const bt = (b.createdOn && b.createdOn.seconds) || 0
            return bt - at
          })
      }, err => console.warn('Missions listener error:', err && err.message))
    this.fetchDatasets()
    // Heartbeat freshness dot rerenders on a coarse clock.
    this.nowTimer = setInterval(() => { this.now = Date.now() }, 15000)
  },
  beforeDestroy () {
    if (this.unsubscribe) this.unsubscribe()
    if (this.nowTimer) clearInterval(this.nowTimer)
    if (this.pathMap) { this.pathMap.remove(); this.pathMap = null }
  },
  methods: {
    // Fetch the chosen dataset's GeoJSON straight from Storage (same path the
    // farm map uses) and validate it against the mission-path contract.
    async loadPath (id) {
      this.path = { loading: !!id, error: '', points: [], datasetId: id }
      if (!id) { this.drawPath(); return }
      const d = this.datasets.find(x => x.id === id)
      try {
        const storagePath = d && d.file && (d.file.geoJsonPath || d.file.path)
        if (!storagePath) throw new Error('This dataset has no GeoJSON file.')
        const url = await firebase.storage().ref(storagePath).getDownloadURL()
        const gj = await (await fetch(url)).json()
        if (this.path.datasetId !== id) return // user picked another one meanwhile
        const r = pathFromGeoJson(gj)
        this.path = { loading: false, error: r.error || '', points: r.points || [], datasetId: id }
      } catch (e) {
        if (this.path.datasetId !== id) return
        this.path = { loading: false, error: 'Could not read this dataset: ' + (e && e.message), points: [], datasetId: id }
      }
      this.$nextTick(() => this.drawPath())
    },
    // Small preview map: the line plus a green start and red end marker so the
    // one thing a line doesn't show on its own — which end is first — is
    // obvious before publishing. Re-drawn when Direction flips.
    drawPath () {
      const pts = this.orientedPoints
      if (!pts.length) return
      const el = document.getElementById('ms-path-map')
      if (!el) return
      const line = turf.lineString(pts)
      const ends = turf.featureCollection([
        turf.point(pts[0], { role: 'start' }),
        turf.point(pts[pts.length - 1], { role: 'end' })
      ])
      const paint = () => {
        const m = this.pathMap
        if (m.getSource('ms-line')) {
          m.getSource('ms-line').setData(line)
          m.getSource('ms-ends').setData(ends)
        } else {
          m.addSource('ms-line', { type: 'geojson', data: line })
          m.addSource('ms-ends', { type: 'geojson', data: ends })
          m.addLayer({ id: 'ms-line-casing', type: 'line', source: 'ms-line', paint: { 'line-color': '#000', 'line-width': 5, 'line-opacity': 0.5 } })
          m.addLayer({ id: 'ms-line', type: 'line', source: 'ms-line', paint: { 'line-color': '#ffc107', 'line-width': 2.5 } })
          m.addLayer({ id: 'ms-ends', type: 'circle', source: 'ms-ends', paint: { 'circle-radius': 8, 'circle-color': ['match', ['get', 'role'], 'start', '#28a745', '#dc3545'], 'circle-stroke-color': '#fff', 'circle-stroke-width': 2 } })
          m.addLayer({ id: 'ms-ends-label', type: 'symbol', source: 'ms-ends', layout: { 'text-field': ['match', ['get', 'role'], 'start', 'START', 'END'], 'text-size': 11, 'text-offset': [0, 1.4], 'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'] }, paint: { 'text-color': '#fff', 'text-halo-color': '#000', 'text-halo-width': 1.2 } })
        }
        m.resize() // the container may have had no size when the map was created
        m.fitBounds(turf.bbox(line), { padding: 40, animate: false, maxZoom: 18 })
      }
      if (!this.pathMap) {
        mapboxgl.accessToken = 'pk.eyJ1IjoibGVyZ3AiLCJhIjoiY2p4bmI1NzNzMGN0MTNjcGx4cjF4eDBtdSJ9.2C0FEHhNZ-jGd7jgIRTrEQ'
        this.pathMap = new mapboxgl.Map({ container: el, style: 'mapbox://styles/mapbox/satellite-v9', center: pts[0], zoom: 16, attributionControl: false })
        this.pathMap.on('load', paint)
      } else if (this.pathMap.isStyleLoaded()) {
        paint()
      } else {
        this.pathMap.once('load', paint)
      }
    },
    async fetchDatasets () {
      try {
        // Single-equality query → no composite index needed; sort client-side.
        const snap = await fb.datasetsCollection
          .where('farmId', '==', this.farmId)
          .limit(200)
          .get()
        this.datasets = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(d => d.file && (d.file.geoJsonPath || d.file.path) && !d.file.raster && !d.trashed)
          .sort((a, b) => ((b.updatedOn && b.updatedOn.seconds) || 0) - ((a.updatedOn && a.updatedOn.seconds) || 0))
      } catch (e) {
        console.warn('Mission dataset list error:', e && e.message)
      }
    },
    datasetName (id) {
      const d = this.datasets.find(x => x.id === id)
      return d ? d.name : (id || '—')
    },
    isFresh (m) {
      const hb = m.lastHeartbeatOn && m.lastHeartbeatOn.seconds ? m.lastHeartbeatOn.seconds * 1000 : 0
      return hb && (this.now - hb) < 2 * 60 * 1000
    },
    async saveDraft () {
      this.savingDraft = true
      try {
        const uid = this.$store.state.currentUser.uid
        await fb.db.collection('missions').add({
          farmId: this.farmId,
          name: this.form.name,
          type: 'follow_track',
          status: 'draft',
          assetId: this.form.assetId || null,
          assetLabel: this.form.assetLabel || null,
          pathDatasetId: this.form.pathDatasetId,
          params: {
            targetSpeedMps: Number(this.form.targetSpeedMps) || 0.5,
            laps: Number(this.form.laps) || 1,
            direction: this.form.direction
          },
          notes: this.form.notes || '',
          createdOn: new Date(),
          createdBy: uid,
          results: null
        })
        this.form.name = ''
        this.form.notes = ''
        this.$store.commit('setAlert', { active: true, message: 'Draft saved. Publish it when the robot should see it.', level: 'alert-success', timeout: 4000 })
      } catch (e) {
        this.$store.commit('setAlert', { active: true, message: 'Could not save the mission: ' + (e && e.message), level: 'alert-danger', timeout: 8000 })
      } finally {
        this.savingDraft = false
      }
    },
    async setStatus (m, status) {
      try {
        const uid = this.$store.state.currentUser.uid
        const patch = { status: status, updatedOn: new Date(), updatedBy: uid }
        if (status === 'ready') {
          patch.publishedOn = new Date()
          patch.publishedBy = uid
          patch.expiresOn = firebase.firestore.Timestamp.fromDate(new Date(Date.now() + 14 * 86400000))
        }
        if (status === 'aborted') patch.abortedOn = new Date()
        await fb.db.collection('missions').doc(m.id).update(patch)
      } catch (e) {
        this.$store.commit('setAlert', { active: true, message: 'Update failed: ' + (e && e.message), level: 'alert-danger', timeout: 8000 })
      }
    },
    async removeMission (m) {
      if (!window.confirm(`Delete mission "${m.name}"? Missions that have run cannot be deleted.`)) return
      try {
        await fb.db.collection('missions').doc(m.id).delete()
      } catch (e) {
        this.$store.commit('setAlert', { active: true, message: 'Delete failed: ' + (e && e.message), level: 'alert-danger', timeout: 8000 })
      }
    },
    viewLive () {
      // Full load of the farm map; ?live=1 tells FarmMenu to focus the live
      // tracks once the count listener sees them (see FarmMenu.vue).
      window.location.href = '/f/' + this.farmId + '?live=1'
    }
  }
}
</script>

<style scoped>
#missions-plugin {
  min-height: 100vh;
  background: #fff;
}
.ms-no-farm {
  padding: 3rem 1rem;
  text-align: center;
}
.ms-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 2px 9px;
  border-radius: 10px;
  background: #e9ecef;
  color: #495057;
}
.ms-ready { background: #d4edda; color: #155724; }
.ms-path-map { height: 260px; border-radius: 6px; overflow: hidden; border: 1px solid #dee2e6; }
.ms-dot { display: inline-block; width: 9px; height: 9px; border-radius: 50%; vertical-align: middle; margin: 0 2px; }
.ms-dot-start { background: #28a745; }
.ms-dot-end { background: #dc3545; }
.ms-claimed { background: #fff3cd; color: #856404; }
.ms-running { background: #cce5ff; color: #004085; }
.ms-complete { background: #d4edda; color: #155724; }
.ms-failed { background: #f8d7da; color: #721c24; }
.ms-aborted { background: #f8d7da; color: #721c24; }
.ms-hb {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #adb5bd;
}
.ms-hb.fresh {
  background: #28a745;
}
</style>
